import React, { useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from '../components/ui/accordion';
import { 
  CheckCircle, 
  Shield, 
  FileText, 
  Users, 
  Clock, 
  MessageCircle,
  ArrowRight,
  Phone,
  Mail,
  Heart,
  Sparkles,
  Scale
} from 'lucide-react';
import { mockData } from '../data/mock';
import WhatsAppWidget from '../components/WhatsAppWidget';
import ChatWidget from '../components/ChatWidget';
import heroImage from '../assets/hero-image.jpg';
import logoImage from '../assets/logo-perea.png';

const MARQUEE_ITEMS = [
  'Cancela tus deudas',
  'Recupera tu tranquilidad',
  'Ley de Segunda Oportunidad',
  'Te escuchamos sin compromiso',
  'Empieza de cero'
];

const Reveal = ({ children, delay = 0, className }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-80px' }}
    transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
  >
    {children}
  </motion.div>
);

const LandingPage = () => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const whatsappNumber = "+34626125436";

  const { scrollY } = useScroll();
  const heroBgY = useTransform(scrollY, [0, 800], [0, 140]);
  
  const handleWhatsAppClick = () => {
    const message = encodeURIComponent("Hola, me gustaría recibir información sobre la Ley de Segunda Oportunidad");
    window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');
  };

  return (
    <div className="landing-page">
      {/* WhatsApp Floating Widget */}
      <WhatsAppWidget />

      {/* Asistente IA flotante */}
      <ChatWidget />
      
      {/* Header */}
      <header className="header-nav">
        <div className="header-container">
          <div className="logo-container">
            <img 
              src={logoImage}
              alt="Perea Abogados" 
              className="logo"
            />
          </div>
          <Button 
            onClick={handleWhatsAppClick}
            className="cta-button-header"
            data-testid="header-contact-btn"
          >
            <MessageCircle className="icon-small" />
            Contactar
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <motion.div 
          className="hero-bg" 
          style={{ backgroundImage: `url(${heroImage})`, y: heroBgY }}
        />
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="hero-text">
            <span className="hero-badge">
              <Sparkles className="icon-tiny" />
              Ley de Segunda Oportunidad
            </span>
            <h1 className="hero-title">
              <span className="hero-line-mask">
                <motion.span
                  className="hero-line"
                  initial={{ y: '110%' }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
                >
                  Un Nuevo Comienzo
                </motion.span>
              </span>
              <span className="hero-line-mask">
                <motion.span
                  className="hero-line hero-line-accent"
                  initial={{ y: '110%' }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.45 }}
                >
                  Es Posible
                </motion.span>
              </span>
            </h1>
            <p className="hero-subtitle">
              Si las deudas te ahogan, existe una solución legal. Cancelamos tus deudas 
              y recuperas tu tranquilidad. Miles de familias ya lo lograron.
            </p>
            <div className="hero-cta-group">
              <Button 
                onClick={handleWhatsAppClick}
                className="cta-button-primary"
                size="lg"
                data-testid="hero-whatsapp-btn"
              >
                <MessageCircle className="icon-small" />
                Consulta por WhatsApp
              </Button>
              <Button 
                variant="outline"
                className="cta-button-secondary"
                size="lg"
                onClick={() => window.location.href = 'tel:+34626125436'}
                data-testid="hero-call-btn"
              >
                <Phone className="icon-small" />
                Llamar Ahora
              </Button>
            </div>
            <div className="hero-stats">
              {mockData.stats.map((stat, index) => (
                <div key={index} className="stat-item">
                  <div className="stat-number">{stat.number}</div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Marquee editorial */}
      <div className="marquee-strip" data-testid="marquee-strip">
        <div className="marquee-track">
          {[0, 1].map((copy) => (
            <div className="marquee-group" key={copy} aria-hidden={copy === 1}>
              {MARQUEE_ITEMS.map((item, i) => (
                <span key={i} className="marquee-item">
                  {item}
                  <span className="marquee-dot"></span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ¿Qué es la Segunda Oportunidad? */}
      <section className="section-light">
        <div className="container">
          <Reveal className="section-header">
            <span className="section-badge">La Solución</span>
            <h2 className="section-title">¿Qué es la Ley de Segunda Oportunidad?</h2>
            <p className="section-description">
              Una oportunidad legal para cancelar tus deudas y empezar de nuevo
            </p>
          </Reveal>
          <div className="benefits-grid">
            {mockData.benefits.map((benefit, index) => (
              <Reveal key={index} delay={index * 0.08}>
                <Card 
                  className={`benefit-card ${hoveredCard === index ? 'card-hovered' : ''}`}
                  onMouseEnter={() => setHoveredCard(index)}
                  onMouseLeave={() => setHoveredCard(null)}
                >
                  <CardHeader>
                    <div className="icon-container-primary">
                      {React.createElement(
                        benefit.icon === 'Shield' ? Shield :
                        benefit.icon === 'Heart' ? Heart :
                        benefit.icon === 'CheckCircle' ? CheckCircle :
                        benefit.icon === 'Scale' ? Scale : Shield
                      )}
                    </div>
                    <CardTitle className="card-title">{benefit.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="card-description">{benefit.description}</p>
                  </CardContent>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Cómo Funciona */}
      <section className="section-accent">
        <div className="container">
          <Reveal className="section-header">
            <span className="section-badge-light">El Proceso</span>
            <h2 className="section-title-light">Cómo Funciona</h2>
            <p className="section-description-light">
              Un proceso sencillo y transparente hacia tu libertad financiera
            </p>
          </Reveal>
          <div className="process-grid">
            {mockData.process.map((step, index) => (
              <Reveal key={index} delay={index * 0.1}>
                <div className="process-step">
                  <div className="process-number">{index + 1}</div>
                  <div className="process-content">
                    <h3 className="process-title">{step.title}</h3>
                    <p className="process-description">{step.description}</p>
                  </div>
                  {index < mockData.process.length - 1 && (
                    <ArrowRight className="process-arrow" />
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Por Qué Elegirnos */}
      <section className="section-light">
        <div className="container">
          <Reveal className="section-header">
            <span className="section-badge">Experiencia y Confianza</span>
            <h2 className="section-title">Por Qué Confiar en Perea Abogados</h2>
          </Reveal>
          <div className="features-grid">
            {mockData.features.map((feature, index) => (
              <Reveal key={index} delay={index * 0.08}>
                <div className="feature-item">
                  <div className="feature-icon">
                    {React.createElement(
                      feature.icon === 'Users' ? Users :
                      feature.icon === 'FileText' ? FileText :
                      feature.icon === 'Clock' ? Clock :
                      feature.icon === 'Shield' ? Shield : Users
                    )}
                  </div>
                  <h3 className="feature-title">{feature.title}</h3>
                  <p className="feature-description">{feature.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonios */}
      <section className="section-testimonials">
        <div className="container">
          <Reveal className="section-header">
            <span className="section-badge">Historias Reales</span>
            <h2 className="section-title">Casos de Éxito</h2>
            <p className="section-description">
              Personas como tú que recuperaron su vida
            </p>
          </Reveal>
          <div className="testimonials-grid">
            {mockData.testimonials.map((testimonial, index) => (
              <Reveal key={index} delay={index * 0.08}>
                <Card className="testimonial-card">
                  <CardContent className="testimonial-content">
                    <div className="quote-mark">"</div>
                    <p className="testimonial-text">{testimonial.text}</p>
                    <div className="testimonial-author">
                      <div className="author-avatar">{testimonial.initials}</div>
                      <div>
                        <div className="author-name">{testimonial.name}</div>
                        <div className="author-detail">{testimonial.detail}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-light">
        <div className="container-narrow">
          <Reveal className="section-header">
            <span className="section-badge">Preguntas Frecuentes</span>
            <h2 className="section-title">¿Tienes Dudas?</h2>
            <p className="section-description">
              Resolvemos las preguntas más comunes
            </p>
          </Reveal>
          <Reveal>
            <Accordion type="single" collapsible className="faq-accordion">
              {mockData.faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="faq-question">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="faq-answer">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-cta">
        <div className="container">
          <Reveal className="cta-content">
            <h2 className="cta-title">¿Listo Para Tu Segunda Oportunidad?</h2>
            <p className="cta-subtitle">
              No esperes más. Cada día cuenta. Escríbenos ahora y comienza tu camino hacia la libertad financiera.
            </p>
            <Button 
              onClick={handleWhatsAppClick}
              className="cta-button-final"
              size="lg"
              data-testid="final-whatsapp-btn"
            >
              <MessageCircle className="icon-medium" />
              Consulta por WhatsApp
            </Button>
            <p className="cta-note">Sin compromiso • Confidencial</p>
          </Reveal>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-column">
              <img 
                src={logoImage}
                alt="Perea Abogados" 
                className="footer-logo"
              />
              <p className="footer-text">
                Especialistas en Ley de Segunda Oportunidad y Concurso de Acreedores. 
                Tu tranquilidad es nuestra prioridad.
              </p>
            </div>
            <div className="footer-column">
              <h4 className="footer-title">Contacto</h4>
              <div className="footer-links">
                <a href={`https://wa.me/${whatsappNumber}`} className="footer-link">
                  <MessageCircle className="icon-tiny" />
                  WhatsApp
                </a>
                <a href="tel:+34626125436" className="footer-link">
                  <Phone className="icon-tiny" />
                  +34 626 125 436
                </a>
                <a href="mailto:madrid@perea-abogados.com" className="footer-link">
                  <Mail className="icon-tiny" />
                  madrid@perea-abogados.com
                </a>
              </div>
            </div>
            <div className="footer-column">
              <h4 className="footer-title">Servicios</h4>
              <div className="footer-links">
                <a href="#" className="footer-link">Ley Segunda Oportunidad</a>
                <a href="#" className="footer-link">Concurso de Acreedores</a>
                <a href="#" className="footer-link">Cancelación de Deudas</a>
                <a href="#" className="footer-link">Asesoramiento Legal</a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2024 Perea Abogados. Todos los derechos reservados.</p>
            <div className="footer-legal">
              <a href="#">Política de Privacidad</a>
              <a href="#">Aviso Legal</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
